/**
 * Database Connection API Service
 *
 * API methods for database connection operations including creation, access grants, and management.
 * Updated to use standardized API response format.
 */

import apiClient from "./client";
import type { ApiSuccessResponse, ApiCollectionResponse } from "../types/api.types";
import type {
  DatabaseConnection,
  DatabaseConnectionCreateDTO,
  DatabaseConnectionUpdateDTO,
  DatabaseConnectionListQuery,
} from "../types/database-connection.types";

// ============================================================================
// DATABASE CONNECTION SERVICE
// ============================================================================

export const databaseConnectionService = {
  /**
   * List all database connections for a tenant
   */
  listDatabaseConnections: async (
    tenantId: string,
    query?: DatabaseConnectionListQuery
  ): Promise<DatabaseConnection[]> => {
    const response = await apiClient.get<ApiCollectionResponse<DatabaseConnection>>(
      `/tenants/${tenantId}/database-connections`,
      { params: query }
    );
    return response.data.data;
  },

  /**
   * Get database connection by ID
   */
  getDatabaseConnection: async (tenantId: string, dbConnectionId: string): Promise<DatabaseConnection> => {
    const response = await apiClient.get<ApiSuccessResponse<DatabaseConnection>>(
      `/tenants/${tenantId}/database-connections/${dbConnectionId}`
    );
    return response.data.data;
  },

  /**
   * Create a new database connection
   */
  createDatabaseConnection: async (
    tenantId: string,
    data: DatabaseConnectionCreateDTO
  ): Promise<DatabaseConnection> => {
    const response = await apiClient.post<ApiSuccessResponse<DatabaseConnection>>(
      `/tenants/${tenantId}/database-connections`,
      data
    );
    return response.data.data;
  },

  /**
   * Update database connection
   */
  updateDatabaseConnection: async (
    tenantId: string,
    dbConnectionId: string,
    data: DatabaseConnectionUpdateDTO
  ): Promise<DatabaseConnection> => {
    const response = await apiClient.put<ApiSuccessResponse<DatabaseConnection>>(
      `/tenants/${tenantId}/database-connections/${dbConnectionId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete database connection
   */
  deleteDatabaseConnection: async (
    tenantId: string,
    dbConnectionId: string
  ): Promise<void> => {
    await apiClient.delete(`/tenants/${tenantId}/database-connections/${dbConnectionId}`);
  },

  /**
   * Rotate SSH key pair for a database connection
   * Generates a new SSH key pair and returns the updated connection with new public key
   */
  rotateSshKey: async (
    tenantId: string,
    dbConnectionId: string
  ): Promise<DatabaseConnection> => {
    const response = await apiClient.post<ApiSuccessResponse<DatabaseConnection>>(
      `/tenants/${tenantId}/database-connections/${dbConnectionId}/rotate-ssh-key`
    );
    return response.data.data;
  },

  /**
   * Test connectivity to a database connection
   * Returns connection test result with success status, message, and latency
   */
  testDatabaseConnection: async (
    tenantId: string,
    dbConnectionId: string
  ): Promise<{ success: boolean; message: string; latencyMs?: number }> => {
    const response = await apiClient.post<
      ApiSuccessResponse<{ success: boolean; message: string; latencyMs?: number }>
    >(`/tenants/${tenantId}/database-connections/${dbConnectionId}/test`);
    return response.data.data;
  },
};

export default databaseConnectionService;
